import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Clock, ShieldCheck, Zap } from "lucide-react";
import { GlowOrb } from "@/components/common/GlowOrb";
import Seo from "@/components/seo/Seo";

/**
 * /quote — landing page that immediately opens the global QuoteDrawer.
 * We use the SAME drawer form used everywhere else on the site so the
 * fields, validation and submission flow are identical.
 */
export default function QuoteRequest() {
  useEffect(() => {
    // Fire on next tick so the layout is mounted and the drawer listener is attached
    const t = setTimeout(() => {
      window.dispatchEvent(new CustomEvent("open-quote-drawer"));
    }, 200);
    return () => clearTimeout(t);
  }, []);

  const openDrawer = () => window.dispatchEvent(new CustomEvent("open-quote-drawer"));

  const bullets = [
    { icon: Clock, title: "48-hour proposal", copy: "A tailored architecture, timeline and price — turned around within two business days." },
    { icon: ShieldCheck, title: "Senior team, no hand-offs", copy: "You'll speak with the people who'll actually build it — not account managers." },
    { icon: Zap, title: "Fixed-scope MVPs in 6-10 weeks", copy: "We ship a working slice fast, then iterate against real usage — not slide decks." },
  ];

  return (
    <div className="relative min-h-[80vh]" data-testid="quote-page">
      <Seo
        title="Request a quote"
        description="Tell us about your project — 48-hour tailored proposal from CloudLab Technologies. AI, custom software, cloud and staff augmentation for teams in the UK, US, Germany, KSA, UAE and Pakistan."
        keywords="request quote, AI development quote, software development quote, CloudLab quote"
      />
      <GlowOrb className="top-10 -left-10" color="primary" size={520} />
      <GlowOrb className="bottom-0 -right-10" color="secondary" size={520} />

      <section className="relative max-w-6xl mx-auto px-6 pt-16 md:pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Get a quote</div>
          <h1 className="mt-4 font-heading font-black text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02]">
            Let&apos;s cost your next <span className="text-primary">AI-powered</span> build.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Answer 9 short questions and a senior engineer will email you a tailored proposal within 48 hours.
            No sales calls, no fluff — just a clear plan, timeline and price.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={openDrawer}
              data-testid="quote-page-open-drawer"
              className="rounded-full px-7 py-3.5 bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center gap-2 shadow-[0_15px_40px_-15px_hsl(var(--primary)/0.6)] hover:opacity-90 transition-opacity"
            >
              Start the form <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to="/contact"
              className="rounded-full px-6 py-3.5 border border-border text-sm inline-flex items-center gap-2 hover:bg-foreground/5"
            >
              Prefer email? Contact us
            </Link>
          </div>
        </motion.div>

        {/* Value props */}
        <div className="mt-16 grid md:grid-cols-3 gap-4">
          {bullets.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + i * 0.08, duration: 0.5 }}
              className="glass rounded-3xl p-6"
            >
              <div className="w-11 h-11 rounded-2xl bg-primary/10 grid place-items-center">
                <b.icon className="w-5 h-5 text-primary" />
              </div>
              <div className="mt-5 font-heading font-bold text-lg">{b.title}</div>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.copy}</p>
            </motion.div>
          ))}
        </div>

        {/* What we'll ask */}
        <div className="mt-14 rounded-3xl border border-border bg-card/40 backdrop-blur p-8 md:p-10">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">What we&apos;ll ask</div>
          <h2 className="mt-3 font-heading font-bold text-2xl md:text-3xl">Just the essentials — 2 minutes.</h2>
          <ul className="mt-6 grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            {[
              "Your name and business email",
              "Company (optional)",
              "Phone with dial code",
              "Country you're operating in",
              "Which service you need",
              "Your ideal timeline",
              "A short project description",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={openDrawer}
            data-testid="quote-page-open-drawer-2"
            className="mt-8 rounded-full px-6 py-3 bg-foreground text-background text-sm font-medium inline-flex items-center gap-2 hover:opacity-90"
          >
            Open the form <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>
    </div>
  );
}
